import type { MessageKey } from '@dijitalapp/i18n';
import type {
  AdminAiUsageDto,
  AdminAuditEntryDto,
  AdminFlagDto,
  AdminPlanDto,
  AdminSecurityEventDto,
  AdminSecuritySummaryDto,
  AdminSettingsDto,
  AdminSystemDto,
  RegistrationMode,
} from '@dijitalapp/shared';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';

/** Admin console operations (ADR-023). The server re-checks every permission; this only renders. */

function useLoad<T>(path: string) {
  const { i18n } = useApp();
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => api<T>('GET', path).then(setData, (err: unknown) => setError(i18n.t(errorMessageKey(err)))), [path, i18n]);
  useEffect(() => {
    let alive = true;
    api<T>('GET', path).then(
      (d) => alive && setData(d),
      (err: unknown) => alive && setError(i18n.t(errorMessageKey(err))),
    );
    return () => {
      alive = false;
    };
  }, [path, i18n]);
  return { data, setData, error, setError, reload };
}

function ErrorLine({ error }: { error: string | null }) {
  return error ? (
    <p className="alert alert-error" role="alert">
      {error}
    </p>
  ) : null;
}

/** Every configuration change asks for a reason, which goes into the audit log. */
function askReason(t: (k: MessageKey) => string): string | null {
  const r = window.prompt(t('adminOps.reasonPrompt'));
  if (r === null) return null;
  if (r.trim().length < 5) {
    window.alert(t('adminOps.reasonShort'));
    return null;
  }
  return r.trim();
}

const json = (v: unknown) => (v === null || v === undefined ? '' : JSON.stringify(v));

// ---------- configuration ----------

export function ConfigTab() {
  const { i18n } = useApp();
  const { t } = i18n;
  const flags = useLoad<{ flags: AdminFlagDto[] }>('/admin/flags');
  const plans = useLoad<{ plans: AdminPlanDto[] }>('/admin/plans');
  const settings = useLoad<AdminSettingsDto>('/admin/settings');
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const change = async (fn: (reason: string) => Promise<void>) => {
    const reason = askReason(t);
    if (!reason) return;
    setError(null);
    try {
      await fn(reason);
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  return (
    <section className="stack">
      <p className="hint">{t('adminOps.config.note')}</p>
      <ErrorLine error={error ?? flags.error ?? plans.error ?? settings.error} />

      {settings.data && (
        <div className="card stack">
          <h2>{t('adminOps.settings')}</h2>
          <div className="row">
            <label htmlFor="reg-mode">{t('adminOps.registrationMode')}</label>
            <select
              id="reg-mode"
              className="input"
              style={{ width: 'auto' }}
              value={settings.data.registrationMode}
              onChange={(e) => {
                const mode = e.target.value as RegistrationMode;
                void change(async (reason) => settings.setData(await api<AdminSettingsDto>('PUT', '/admin/settings/registration-mode', { mode, reason })));
              }}
            >
              {(['open', 'invite', 'closed'] as const).map((m) => (
                <option key={m} value={m}>
                  {t(`adminOps.registration.${m}` as MessageKey)}
                </option>
              ))}
            </select>
            <span className="muted">{t(settings.data.registrationModeSource === 'console' ? 'adminOps.source.console' : 'adminOps.source.env')}</span>
          </div>
          {settings.data.remoteConfig.map((r) => (
            <form
              key={r.key}
              className="row"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                const value = Number(drafts[r.key] ?? r.value);
                void change(async (reason) => settings.setData(await api<AdminSettingsDto>('PUT', `/admin/settings/remote-config/${r.key}`, { value, reason })));
              }}
            >
              <label htmlFor={`rc-${r.key}`} style={{ minWidth: 200 }}>
                <code>{r.key}</code>
              </label>
              <input
                id={`rc-${r.key}`}
                className="input"
                type="number"
                min={r.min}
                max={r.max}
                style={{ width: 140 }}
                value={drafts[r.key] ?? String(r.value)}
                onChange={(e) => setDrafts({ ...drafts, [r.key]: e.target.value })}
              />
              <span className="muted">{t('adminOps.range', { min: r.min, max: r.max, def: r.defaultValue })}</span>
              <button className="btn">{t('adminOps.save')}</button>
            </form>
          ))}
        </div>
      )}

      {flags.data && (
        <div className="card stack">
          <h2>{t('adminOps.flags')}</h2>
          <p className="hint">{t('adminOps.flagsNote')}</p>
          {flags.data.flags.map((f) => (
            <label key={f.key} className="toggle-row">
              <span style={{ minWidth: 0 }}>
                <code>{f.key}</code>
                {f.description && <span className="muted"> · {f.description}</span>}
                {f.overrides > 0 && <span className="badge">{t('adminOps.overrides', { count: f.overrides })}</span>}
              </span>
              <span className="spacer" />
              <input
                type="checkbox"
                checked={f.enabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  void change(async (reason) => flags.setData(await api<{ flags: AdminFlagDto[] }>('PUT', `/admin/flags/${f.key}`, { enabled, reason })));
                }}
              />
            </label>
          ))}
        </div>
      )}

      {plans.data?.plans.map((p) => (
        <div key={p.key} className="card stack">
          <h2>
            {t('adminOps.plan')}: {p.name} <span className="muted">({p.key})</span>
          </h2>
          {p.entitlements.map((e) => {
            const id = `${p.key}:${e.key}`;
            return (
              <form
                key={id}
                className="row"
                onSubmit={(ev: FormEvent) => {
                  ev.preventDefault();
                  const raw = drafts[id] ?? String(e.value);
                  const value = typeof e.value === 'boolean' ? raw === 'true' : Number(raw);
                  void change(async (reason) => plans.setData(await api<{ plans: AdminPlanDto[] }>('PUT', `/admin/plans/${p.key}/entitlements/${e.key}`, { value, reason })));
                }}
              >
                <label htmlFor={`ent-${id}`} style={{ minWidth: 240 }}>
                  <code>{e.key}</code>
                </label>
                {typeof e.value === 'boolean' ? (
                  <select id={`ent-${id}`} className="input" style={{ width: 120 }} value={drafts[id] ?? String(e.value)} onChange={(x) => setDrafts({ ...drafts, [id]: x.target.value })}>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                ) : (
                  <input id={`ent-${id}`} className="input" type="number" min={0} style={{ width: 140 }} value={drafts[id] ?? String(e.value)} onChange={(x) => setDrafts({ ...drafts, [id]: x.target.value })} />
                )}
                <button className="btn">{t('adminOps.save')}</button>
              </form>
            );
          })}
        </div>
      ))}

      <div className="card stack">
        <h2>{t('adminOps.secretsTitle')}</h2>
        <p className="hint">{t('adminOps.secretsNote')}</p>
      </div>
    </section>
  );
}

// ---------- audit ----------

export function AuditTab() {
  const { i18n } = useApp();
  const { t } = i18n;
  const [filters, setFilters] = useState({ action: '', actor: '', resourceType: '' });
  const [entries, setEntries] = useState<AdminAuditEntryDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const query = (before?: string) => {
    const p = new URLSearchParams({ limit: '50' });
    for (const [k, v] of Object.entries(filters)) if (v.trim()) p.set(k, v.trim());
    if (before) p.set('before', before);
    return `/admin/audit?${p}`;
  };
  const search = (before?: string) =>
    api<{ entries: AdminAuditEntryDto[] }>('GET', query(before)).then(
      (r) => setEntries(before ? [...(entries ?? []), ...r.entries] : r.entries),
      (err: unknown) => setError(t(errorMessageKey(err))),
    );
  useEffect(() => {
    let alive = true;
    api<{ entries: AdminAuditEntryDto[] }>('GET', '/admin/audit?limit=50').then((r) => alive && setEntries(r.entries), (err: unknown) => alive && setError(t(errorMessageKey(err))));
    return () => {
      alive = false;
    };
  }, [t]);

  return (
    <section className="stack">
      <form
        className="card row"
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
      >
        {(['action', 'actor', 'resourceType'] as const).map((k) => (
          <span key={k} className="field">
            <label htmlFor={`af-${k}`}>{t(`adminOps.audit.${k}` as MessageKey)}</label>
            <input id={`af-${k}`} className="input" maxLength={80} value={filters[k]} onChange={(e) => setFilters({ ...filters, [k]: e.target.value })} />
          </span>
        ))}
        <button className="btn btn-primary">{t('adminOps.search')}</button>
      </form>
      <ErrorLine error={error} />
      {entries?.length === 0 && <p className="muted">{t('adminOps.empty')}</p>}
      <div className="card stack" style={{ gap: 0 }}>
        {entries?.map((e) => (
          <div key={e.id} className="stack" style={{ borderBottom: '1px solid var(--border)', padding: 'var(--space-2) 0', gap: 'var(--space-1)' }}>
            <button className="link-button" type="button" style={{ textAlign: 'left' }} aria-expanded={open === e.id} onClick={() => setOpen(open === e.id ? null : e.id)}>
              <span className="muted">{i18n.formatDate(e.createdAt)}</span> · <strong>{e.action}</strong> · {e.actor ? `@${e.actor.username}` : e.actorType} ·{' '}
              <span className="muted">
                {e.resourceType}
                {e.resourceId && `:${e.resourceId}`}
              </span>
            </button>
            {open === e.id && (
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 'var(--text-xs)' }}>
                {t('adminOps.audit.before')}: {json(e.before)}
                {'\n'}
                {t('adminOps.audit.after')}: {json(e.after)}
                {'\n'}IP: {e.ip ?? '—'} · request: {e.requestId ?? '—'}
              </pre>
            )}
          </div>
        ))}
      </div>
      {entries && entries.length > 0 && entries.length % 50 === 0 && (
        <button className="btn" type="button" onClick={() => void search(entries.at(-1)!.createdAt)}>
          {t('adminOps.more')}
        </button>
      )}
    </section>
  );
}

// ---------- security ----------

export function SecurityTab() {
  const { i18n } = useApp();
  const { t } = i18n;
  const [kind, setKind] = useState('');
  const { data, error } = useLoad<{ events: AdminSecurityEventDto[]; summary: AdminSecuritySummaryDto }>(`/admin/security-events?limit=100${kind ? `&kind=${encodeURIComponent(kind)}` : ''}`);
  return (
    <section className="stack">
      <ErrorLine error={error} />
      {data && (
        <div className="card stack">
          <h2>{t('adminOps.security.summary')}</h2>
          {data.summary.kinds.length === 0 && <p className="muted">{t('adminOps.empty')}</p>}
          {data.summary.kinds.map((k) => (
            <button key={k.kind} type="button" className="toggle-row link-button" aria-pressed={kind === k.kind} onClick={() => setKind(kind === k.kind ? '' : k.kind)}>
              <code>{k.kind}</code>
              <span className="spacer" />
              <span className="badge">{t('adminOps.security.counts', { d1: k.last24h, d7: k.last7d })}</span>
            </button>
          ))}
        </div>
      )}
      <div className="card stack" style={{ gap: 0 }}>
        {data?.events.map((e) => (
          <div key={e.id} style={{ borderBottom: '1px solid var(--border)', padding: 'var(--space-2) 0' }}>
            <span className="muted">{i18n.formatDate(e.createdAt)}</span> · <strong>{e.kind}</strong> · {e.user ? `@${e.user.username}` : '—'} · {e.ip ?? '—'}
            {json(e.detail) !== '{}' && <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>{json(e.detail)}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

// ---------- AI ----------

export function AiUsageTab() {
  const { i18n } = useApp();
  const { t } = i18n;
  const [days, setDays] = useState(7);
  const { data, error } = useLoad<AdminAiUsageDto>(`/admin/ai-usage?days=${days}`);
  const max = Math.max(1, ...(data?.daily.map((d) => d.requests) ?? [1]));
  return (
    <section className="stack">
      <div className="row" role="group" aria-label={t('adminOps.ai.period')}>
        {[1, 7, 30].map((d) => (
          <button key={d} type="button" className={`btn ${days === d ? 'btn-primary' : ''}`} aria-pressed={days === d} onClick={() => setDays(d)}>
            {t('adminOps.ai.days', { count: d })}
          </button>
        ))}
      </div>
      <ErrorLine error={error} />
      {data && (
        <>
          <div className="card stack">
            <p>
              {t('adminOps.ai.provider')}: <strong>{data.provider === 'off' ? t('adminOps.ai.off') : `${data.provider} · ${data.model}`}</strong>
            </p>
            <p>{t('adminOps.ai.safety', { count: data.tutorSafetyReplies })}</p>
            {data.byAction.length === 0 && <p className="muted">{t('adminOps.empty')}</p>}
            {data.byAction.map((a) => (
              <div key={a.action} className="toggle-row">
                <code>{a.action}</code>
                <span className="spacer" />
                <span className="muted">
                  {t('adminOps.ai.line', {
                    requests: a.requests,
                    ok: a.ok,
                    refused: a.refused,
                    errors: a.errors,
                    users: a.users,
                    tokens: (a.inputTokens + a.outputTokens).toLocaleString(i18n.locale),
                    latency: a.avgLatencyMs ?? '—',
                  })}
                </span>
              </div>
            ))}
          </div>
          {data.daily.length > 0 && (
            <div className="card stack" style={{ gap: 'var(--space-1)' }}>
              <h2>{t('adminOps.ai.daily')}</h2>
              {data.daily.map((d) => (
                <div key={d.day} className="row" style={{ flexWrap: 'nowrap' }}>
                  <span className="muted" style={{ width: 100 }}>
                    {d.day}
                  </span>
                  <div role="img" aria-label={`${d.day}: ${d.requests}`} style={{ height: 10, width: `${(d.requests / max) * 100}%`, minWidth: 2, background: 'var(--accent)', borderRadius: 4 }} />
                  <span>{d.requests}</span>
                </div>
              ))}
            </div>
          )}
          {data.topUsers.length > 0 && (
            <div className="card stack">
              <h2>{t('adminOps.ai.topUsers')}</h2>
              {data.topUsers.map((u) => (
                <div key={u.id} className="toggle-row">
                  <span>@{u.username}</span>
                  <span className="spacer" />
                  <span>{u.requests}</span>
                </div>
              ))}
              <p className="hint">{t('adminOps.ai.privacy')}</p>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ---------- system ----------

const STATE_ICON: Record<string, string> = { ok: '🟢', degraded: '🟡', fail: '🔴', off: '⚪' };

export function SystemTab() {
  const { i18n } = useApp();
  const { t } = i18n;
  const { data, error, reload } = useLoad<AdminSystemDto>('/admin/system');
  return (
    <section className="stack">
      <div className="row">
        <span className="spacer" />
        <button className="btn" type="button" onClick={() => void reload()}>
          {t('adminOps.refresh')}
        </button>
      </div>
      <ErrorLine error={error} />
      {data && (
        <>
          <div className="card stack">
            {data.checks.map((c) => (
              <div key={c.name} className="toggle-row">
                <span>
                  {STATE_ICON[c.state]} <strong>{c.name}</strong>
                </span>
                <span className="spacer" />
                <span className="muted">
                  {t(`adminOps.state.${c.state}` as MessageKey)}
                  {c.detail && ` · ${c.detail}`}
                </span>
              </div>
            ))}
          </div>
          <div className="card stack">
            <p>{t('adminOps.system.release', { release: data.release, node: data.nodeVersion })}</p>
            <p>{t('adminOps.system.process', { uptime: Math.round(data.uptimeSeconds / 60), memory: data.memoryMb })}</p>
            <p>{t('adminOps.system.migrations', { count: data.migrations.applied, latest: data.migrations.latest ?? '—' })}</p>
            <p>{t('adminOps.system.outbox', { pending: data.outbox.pending, oldest: data.outbox.oldestSeconds ?? 0 })}</p>
            <p>{t('adminOps.system.db', { size: data.database.sizeMb })}</p>
            <p>
              {t('adminOps.system.counts', {
                users: data.counts.users,
                newUsers: data.counts.usersLast24h,
                messages: data.counts.messagesLast24h,
                sockets: data.counts.realtimeConnectionsThisNode,
              })}
            </p>
            <p className="hint">{t('adminOps.system.note')}</p>
          </div>
        </>
      )}
    </section>
  );
}
